import dayjs from 'dayjs';
import { createContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { SessionState, SurveyViewModel } from './SessionState';

type TelemetryContextModel = {
    logFileAccess: (filename: string | null | undefined, sourceKey: string) => void;
    logSiteDetailsViewed: (reference: string | null | undefined) => void;
    logSiteListExamined: (sessionState: SessionState) => void;
    logTripRateCalculationRun: (sessionState: SessionState) => void;
    logRankOrderRun: (sessionState: SessionState, options: RankOrderRunOptions) => void;
};

const NOOP = (..._args: unknown[]) => { };

export const TelemetryContext = createContext<TelemetryContextModel>({
    logFileAccess: NOOP,
    logSiteDetailsViewed: NOOP,
    logSiteListExamined: NOOP,
    logTripRateCalculationRun: NOOP,
    logRankOrderRun: NOOP
});

type PageVisit = {
    pageKey: string;
    startedAt: number;
};

type RankOrderRunOptions = {
    isPeakHour: boolean;
    timePairs: Array<{ start: number; end: number }>;
};

type TelemetrySearchType =
    | 'SiteDetailsViewed'
    | 'SiteListExamined'
    | 'TripRateCalculationRun'
    | 'RankOrderRun';

export function extractTelemetryFileName(value: string | null | undefined) {
    if (!value) {
        return null;
    }

    try {
        const url = new URL(value, window.location.origin);
        const pathPart = url.pathname.split('/').filter(Boolean).pop();
        return pathPart ? decodeURIComponent(pathPart) : null;
    } catch {
        const pathPart = value.split(/[\\/]/).filter(Boolean).pop();
        return pathPart ? decodeURIComponent(pathPart) : null;
    }
}

function getPageKey(pathname: string) {
    const routes: Array<{ match: (path: string) => boolean; key: string }> = [
        { match: path => path === '/cambourne', key: 'Cambourne' },
        { match: path => path.startsWith('/travl'), key: 'Travl' },
        { match: path => path === '/help', key: 'Help' },
        { match: path => path === '/library', key: 'Library' },
        { match: path => path === '/settings', key: 'Settings' },
        { match: path => path === '/softwarearchive', key: 'SoftwareArchive' },
        { match: path => path === '/legacyfiles', key: 'LegacyFiles' },
        { match: path => path === '/timeseriesanalysis', key: 'TimeSeriesAnalysis' },
        { match: path => path.startsWith('/triprate'), key: 'TripRate' },
        { match: path => path === '/site', key: 'Site' },
        { match: path => path === '/sitelist', key: 'SiteList' }
    ];

    return routes.find(route => route.match(pathname))?.key ?? null;
}

function roundDurationSeconds(startedAt: number, endedAt: number) {
    const durationMs = Math.max(0, endedAt - startedAt);
    return Math.round(durationMs / 1000);
}

async function postJson(path: string, body: object) {
    try {
        await fetch(path, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });
    } catch {
        // Telemetry is best-effort only.
    }
}

function trimOrNull(value: string | null | undefined) {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
}

function normalizeDateValue(value: unknown) {
    if (value == null) {
        return undefined;
    }

    if (dayjs.isDayjs(value)) {
        return value.format('YYYY-MM-DD');
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? undefined : dayjs(value).format('YYYY-MM-DD');
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
            return undefined;
        }

        const looksLikeDate =
            trimmed.includes('T') ||
            /^\d{4}-\d{1,2}-\d{1,2}/.test(trimmed) ||
            /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed);

        if (!looksLikeDate) {
            return undefined;
        }

        const parsed = dayjs(trimmed);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD') : trimmed;
    }

    return undefined;
}

function normalizeTelemetryValue(value: unknown): unknown {
    if (value == null) {
        return undefined;
    }

    const normalizedDate = normalizeDateValue(value);
    if (normalizedDate !== undefined) {
        return normalizedDate;
    }

    if (Array.isArray(value)) {
        const normalized = value
            .map(item => normalizeTelemetryValue(item))
            .filter(item => item !== undefined);

        return normalized.length > 0 ? normalized : undefined;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed ? trimmed : undefined;
    }

    if (typeof value === 'number') {
        return Number.isNaN(value) ? undefined : value;
    }

    if (typeof value === 'boolean') {
        return value;
    }

    return value;
}

function flattenTelemetryObject(target: Record<string, unknown>, prefix: string, value: unknown) {
    const normalized = normalizeTelemetryValue(value);
    if (normalized === undefined) {
        return;
    }

    if (Array.isArray(normalized)) {
        target[prefix] = normalized;
        return;
    }

    if (typeof normalized === 'object' && normalized !== null) {
        Object.entries(normalized as Record<string, unknown>).forEach(([key, childValue]) => {
            const nextPrefix = prefix ? `${prefix}.${key}` : key;
            flattenTelemetryObject(target, nextPrefix, childValue);
        });
        return;
    }

    target[prefix] = normalized;
}

function extractLandUseParts(reference: string | null | undefined) {
    const trimmed = trimOrNull(reference);
    if (!trimmed) {
        return { reference: null, landUse: null, subUse: null };
    }

    const parts = trimmed.split('-');
    return {
        reference: trimmed,
        landUse: trimOrNull(parts[1]),
        subUse: trimOrNull(parts[2])
    };
}

function getEffectiveDateRange(sessionState: SessionState) {
    return {
        StartDate: sessionState.landusePage.startDate ?? sessionState.primaryFilters.startDate,
        EndDate: sessionState.landusePage.endDate ?? sessionState.primaryFilters.endDate
    };
}

function buildSummaryFilterParameters(sessionState: SessionState) {
    const flattened: Record<string, unknown> = {};

    flattenTelemetryObject(flattened, '', {
        TripRateType: sessionState.tripRateType,
        ResidentialUnitTypes: sessionState.residentialUnitTypes,
        DateRange: getEffectiveDateRange(sessionState),
        PrimaryFilters: {
            ...sessionState.primaryFilters,
            startDate: undefined,
            endDate: undefined
        },
        SecondaryFilters: sessionState.secondaryFilters,
        LandUsePage: {
            ...sessionState.landusePage,
            startDate: undefined,
            endDate: undefined,
            sites: undefined
        }
    });

    return flattened;
}

function extractRelatedReference(reasonText: string) {
    const match = reasonText.match(/by\s+(.+)$/i);
    return match?.[1]?.trim() || undefined;
}

function getNormalizedReason(
    item: SurveyViewModel,
    effectiveReason: string | null,
    type: 'site' | 'survey'
) {
    if (item.manuallyRemoved) {
        return {
            reasonCode: type === 'site' ? 'ManualSiteDeselection' : 'ManualSurveyDeselection',
            reasonText: effectiveReason || undefined
        };
    }

    if (effectiveReason?.toLowerCase().includes('re-surveyed by')) {
        return {
            reasonCode: 'ResurveyExcluded',
            reasonText: effectiveReason,
            relatedReference: extractRelatedReference(effectiveReason)
        };
    }

    if (effectiveReason?.toLowerCase().includes('most recent')) {
        return {
            reasonCode: 'LatestSurveyOnly',
            reasonText: effectiveReason,
            relatedReference: extractRelatedReference(effectiveReason)
        };
    }

    if (effectiveReason?.toLowerCase().includes('busiest survey')) {
        return {
            reasonCode: 'BusiestSurveyOnly',
            reasonText: effectiveReason,
            relatedReference: extractRelatedReference(effectiveReason)
        };
    }

    return {
        reasonCode: type === 'site' ? 'SiteDeselected' : 'SurveyDeselected',
        reasonText: effectiveReason || undefined
    };
}

function buildDeselectedSites(items: SurveyViewModel[]) {
    const grouped = new Map<string, SurveyViewModel>();

    items
        .filter(item => !item.siteActive && trimOrNull(item.siteRef))
        .forEach(item => {
            const siteRef = trimOrNull(item.siteRef);
            if (siteRef && !grouped.has(siteRef)) {
                grouped.set(siteRef, item);
            }
        });

    return Array.from(grouped.entries()).map(([siteRef, item]) => {
        const effectiveReason = trimOrNull(item.reasonForSiteRemoval) ?? trimOrNull(item.reasonForSurveyRemoval);
        const normalized = getNormalizedReason(item, effectiveReason, 'site');

        return {
            reference: siteRef,
            manual: item.manuallyRemoved,
            ...normalized
        };
    });
}

function buildDeselectedSurveys(items: SurveyViewModel[]) {
    return items
        .filter(item => !item.surveyActive && trimOrNull(item.surveyRef))
        .map(item => {
            const surveyRef = trimOrNull(item.surveyRef) || '';
            const removedInSiteSelection = item.reasonForSurveyRemoval === 'Removed in Site Selection';
            const effectiveReason = trimOrNull(
                removedInSiteSelection
                    ? item.reasonForSiteRemoval
                    : item.reasonForSurveyRemoval
            ) ?? trimOrNull(item.reasonForSiteRemoval);
            const normalized = getNormalizedReason(item, effectiveReason, removedInSiteSelection ? 'site' : 'survey');

            return {
                reference: surveyRef,
                siteReference: trimOrNull(item.siteRef),
                manual: item.manuallyRemoved,
                ...normalized
            };
        });
}

export default function TelemetryContextProvider({ children }: React.PropsWithChildren<{}>) {
    const location = useLocation();
    const currentPageRef = useRef<PageVisit | null>(null);

    const postSearchEvent = (
        searchType: TelemetrySearchType,
        landUse: string | null,
        subUse: string | null,
        parameters?: Record<string, unknown>
    ) => {
        const body: Record<string, unknown> = {
            searchType,
            landUse,
            subUse
        };

        if (parameters && Object.keys(parameters).length > 0) {
            body.parameters = parameters;
        }

        void postJson('/api/TelemetrySearch/event', body);
    };

    useEffect(() => {
        const now = Date.now();
        const nextPageKey = getPageKey(location.pathname);
        const currentPage = currentPageRef.current;

        if (currentPage) {
            void postJson('/api/TelemetryAccess/page-access', {
                pageKey: currentPage.pageKey,
                durationSeconds: roundDurationSeconds(currentPage.startedAt, now)
            });
        }

        currentPageRef.current = nextPageKey ? { pageKey: nextPageKey, startedAt: now } : null;
    }, [location.pathname]);

    useEffect(() => {
        const handlePageHide = () => {
            const currentPage = currentPageRef.current;
            if (!currentPage) {
                return;
            }

            navigator.sendBeacon(
                '/api/TelemetryAccess/page-access',
                new Blob([JSON.stringify({
                    pageKey: currentPage.pageKey,
                    durationSeconds: roundDurationSeconds(currentPage.startedAt, Date.now())
                })], { type: 'application/json' })
            );
        };

        window.addEventListener('pagehide', handlePageHide);
        return () => window.removeEventListener('pagehide', handlePageHide);
    }, []);

    const logFileAccess = (filename: string | null | undefined, sourceKey: string) => {
        if (!filename) {
            return;
        }

        void postJson('/api/TelemetryAccess/file-access', { filename, sourceKey });
    };

    const logSiteDetailsViewed = (reference: string | null | undefined) => {
        const { reference: trimmedReference, landUse, subUse } = extractLandUseParts(reference);
        if (!trimmedReference) {
            return;
        }

        postSearchEvent('SiteDetailsViewed', landUse, subUse, {
            Reference: trimmedReference
        });
    };

    const logSiteListExamined = (sessionState: SessionState) => {
        postSearchEvent('SiteListExamined', trimOrNull(sessionState.siteListMain), trimOrNull(sessionState.siteListSub), {
            LandUseSelectionType: sessionState.siteListType,
            SearchText: trimOrNull(sessionState.siteListTextSearch),
            LandUseSearch: trimOrNull(sessionState.siteListLandSearch)
        });
    };

    const logTripRateCalculationRun = (sessionState: SessionState) => {
        postSearchEvent('TripRateCalculationRun', trimOrNull(sessionState.tripRateMain), trimOrNull(sessionState.tripRateSub), {
            ...buildSummaryFilterParameters(sessionState),
            DeselectedSites: buildDeselectedSites(sessionState.selectedSitesAndSurveys),
            DeselectedSurveys: buildDeselectedSurveys(sessionState.selectedSitesAndSurveys)
        });
    };

    const logRankOrderRun = (sessionState: SessionState, options: RankOrderRunOptions) => {
        postSearchEvent('RankOrderRun', trimOrNull(sessionState.tripRateMain), trimOrNull(sessionState.tripRateSub), {
            ...buildSummaryFilterParameters(sessionState),
            'RankOrder.IsPeakHour': options.isPeakHour,
            'RankOrder.TimeSelections': options.timePairs.map(pair => `${pair.start}-${pair.end}`),
            DeselectedSites: buildDeselectedSites(sessionState.selectedSitesAndSurveys),
            DeselectedSurveys: buildDeselectedSurveys(sessionState.selectedSitesAndSurveys)
        });
    };

    return (
        <TelemetryContext.Provider value={{ logFileAccess, logSiteDetailsViewed, logSiteListExamined, logTripRateCalculationRun, logRankOrderRun }}>
            {children}
        </TelemetryContext.Provider>
    );
}
